/* eslint react/forbid-prop-types: 0 */
import React, {
  useContext, memo
} from 'react';
import PropTypes from 'prop-types';
import { extend, useDataModel } from '@thd-nucleus/data-sources';
import {
  ProductPod,
  ProductATC,
  ProductRating,
  ProductSample,
  ProductServiceAddOns,
  ProductLink,
  ProductIdentifier, ProductBadge,
  ProductImage, PodSection, ProductCompare,
  ProductCustomOptions, PodSpacer
} from '@thd-olt-component-react/product-pod';
import { AddToQuote } from '@thd-olt-component-react/add-to-quote';
import { FulfillmentPodShipping, FulfillmentPodStore } from '@thd-olt-component-react/fulfillment';
import { SuperSkuMiniSwatches } from '@thd-olt-component-react/super-sku';
import { Price } from '@thd-olt-component-react/price';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { NonBuyableQuoteCenter } from '@thd-olt-component-react/non-buyable-quote-center';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { ProductListContext } from '../ProductListContext';
import { ProductNote, NoteButton } from '../note';
import { QuantityStepper } from '../quantity-stepper/QuantityStepper';
import { isItemEqual, isGeneralMerchProduct } from '../productListHelper';
import { ListViewPlaceHolder } from './ListViewPlaceHolder';

export const ListPodMobile = memo(({
  handleQuantityUpdate,
  itemId,
  podIndex,
  quantity: quantityProp,
  showStoreSku,
  storeId,
  searchKeyword
}) => {

  const {
    productsDisplayed,
    isSharedList,
    removeListItems,
    isRemoving,
    list
  } = useContext(ProductListContext);
  const { isB2BCustomer = false } = useThdCustomer();

  const product = productsDisplayed.find((item) => item.itemId === itemId);
  const noATCFulfillment = isGeneralMerchProduct(product);

  // Show placeholder until product data is loaded via batch observer system
  if (!product) {
    return (
      <ListViewPlaceHolder count={1} isMobile />
    );
  }

  return (
    <ProductPod
      itemId={itemId}
      storeId={storeId}
      padding="sui-px-3 sui-pt-1"
      variables={{ skip: true }}
      analyticsData={{
        parent: 'product-list', position: podIndex
      }}
      render={(pod) => (
        <PodSection padding="sui-p-0" columnAlign alignTop noPadding gap="sui-gap-3">
          {/* Section 1: Image + Product Details (3 columns) */}
          <div className="sui-grid sui-grid-cols-3 sui-gap-1">
            {/* Left column - Image (1/3) */}
            <div className="sui-col-span-1">
              <ProductImage itemId={pod.itemId} />
              <SuperSkuMiniSwatches
                itemId={pod.itemId}
                storeId={storeId}
                onChange={pod.onChange}
                onHover={() => { }}
                numberOfMiniSwatches={3}
              />
            </div>

            {/* Right column - Details (2/3) */}
            <div className="sui-col-span-2 sui-flex sui-flex-col sui-gap-1">
              <PodSpacer padding={['BOTTOM']}>
                <ProductBadge itemId={pod.itemId} storeId={storeId} />
              </PodSpacer>
              <ProductHeader
                brand="inline"
                itemId={pod.itemId}
                brandTitleMaxLine={2}
                disableShopThisCollection
              />
              <div className="sui-flex sui-flex-col">
                <ProductIdentifier itemId={pod.itemId} type="itemId" />
                <ProductIdentifier itemId={pod.itemId} type="model" />
                {showStoreSku && <ProductIdentifier itemId={pod.itemId} type="storeSku" />}
              </div>
              {searchKeyword && (
                <div className="sui-text-primary sui-text-xs sui-font-normal">
                  Searched:
                  {' '}
                  <span className="sui-font-bold">{`"${searchKeyword}"`}</span>
                </div>
              )}
              {/* Fulfillment within product details area */}
            </div>
          </div>
          <div className="sui-flex sui-items-start">
            <FulfillmentPodStore itemId={pod.itemId} storeId={pod.storeId} />
            <FulfillmentPodShipping itemId={pod.itemId} storeId={pod.storeId} />
          </div>

          {/* Section 4: Price/Rating + Cart (2 columns) */}
          <div className="sui-grid sui-grid-cols-2 sui-gap-2 sui-mt-2 sui-items-start">
            {/* Left column - Price & Rating */}
            <div className="sui-flex sui-flex-col sui-gap-1">
              <Price
                itemId={pod.itemId}
                large={false}
                storeId={storeId}
                displayEachUom={false}
                showPreferredPricingBadge
                stackWasPrice
              />
              <ProductRating itemId={pod.itemId} />
            </div>

            {/* Right column - Quantity & Cart */}
            <div className="sui-flex sui-flex-col sui-gap-1">
              <QuantityStepper
                itemId={itemId}
                quantity={quantityProp}
                onQuantityChange={handleQuantityUpdate}
                price={product?.pricing?.value}
                isReadOnly={isSharedList}
              />
              <ProductATC
                itemId={pod.itemId}
                checkGeneric
                quantity={quantityProp}
                noATCFulfillment={noATCFulfillment}
              />
              {isB2BCustomer && (
                <AddToQuote
                  itemId={pod.itemId}
                  quantity={quantityProp}
                  buttonText="Quote"

                />
              )}
            </div>
          </div>

          {/* Section 3: Product Note */}
          <ProductNote itemId={itemId} />
        </PodSection>
      )}
    />
  );
}, isItemEqual);

ListPodMobile.displayName = 'ListPodMobile';

ListPodMobile.propTypes = {
  handleQuantityUpdate: PropTypes.func,
  itemId: PropTypes.string.isRequired,
  podIndex: PropTypes.number,
  quantity: PropTypes.number,
  showStoreSku: PropTypes.bool,
  storeId: PropTypes.string,
  searchKeyword: PropTypes.string,
};

ListPodMobile.defaultProps = {
  handleQuantityUpdate: () => { },
  podIndex: 1,
  quantity: 1,
  showStoreSku: true,
  storeId: '',
  searchKeyword: ''
};

ListPodMobile.dataModel = extend(
  NoteButton,
  ProductPod,
  ProductATC,
  ProductImage,
  ProductLink,
  ProductServiceAddOns,
  Price,
  SuperSkuMiniSwatches,
  ProductRating,
  ProductCompare,
  ProductSample,
  ProductIdentifier,
  ProductHeader,
  ProductBadge,
  FulfillmentPodStore,
  FulfillmentPodShipping,
  ProductCustomOptions,
  NonBuyableQuoteCenter,
  AddToQuote
);
